import styled from 'styled-components';
import React from 'react';

import StepSummary from './StepSummary';
import TimeContainer from './TimeContainer';
import { device } from '@src/common/breakpoints';
import { BackdropModal, Spinner } from '@src/components/reusable';
import Calendar from '@src/components/reusable/Calendar';
import HeaderStepsNavigation from '@src/components/reusable/HeaderStepsNavigation';
import { DetailedSaleItem } from '@src/core/buyerApiClient';
import { useWidgetsTheme as theme } from '@src/core/hooks';
import { convertDatesToCalendarDates } from '@src/common/calendar';
import RescheduleSummary from './RescheduleSummary';
import FeatureFlag from '@src/components/FeatureFlag';
import { RescheduleStep, useRescheduleFlow } from '@src/features/sales/reschedule';

type Props = {
  onCancel: () => void;
  onConfirm: () => void;
  reservationId: string;
  saleItem: DetailedSaleItem;
};

const RescheduleModal = (props: Props) => {
  const { reservationId, saleItem, onConfirm, onCancel } = props;
  const { product } = saleItem;

  const actionColor = theme('actionColor');

  const {
    currentStep,
    steps,
    visible,
    date,
    time,
    schedulingsByDate,
    scheduleTimes,
    chargeSummary,
    requiresAdditionalPayment,
    isLastStep,
    isStepComplete,
    isLoadingOptions,
    isRescheduleOngoing,
    selectDate,
    selectTime,
    goToNextStep,
    goToPreviousStep,
    cancel,
  } = useRescheduleFlow({ reservationId, saleItem, onConfirm });

  const convertedRescheduleDates = React.useMemo(
    () => convertDatesToCalendarDates(schedulingsByDate),
    [schedulingsByDate],
  );

  const handleCancelReschedule = () => {
    onCancel();
    cancel();
  };

  return (
    <BackdropModal visible={visible} closeModal={handleCancelReschedule}>
      <Wrapper>
        <HeaderStepsNavigation title={product.name} stepType='reschedule' currentStep={currentStep} steps={steps} />
        <Container>
          {currentStep === RescheduleStep.Date ? (
            <Calendar
              dates={convertedRescheduleDates}
              pending={Object.keys(schedulingsByDate).length === 0 && isLoadingOptions}
              defaultDateReference={date}
              onDateSelect={selectDate}
              tariffToShowThePrice={null}
            />
          ) : null}

          {!isRescheduleOngoing && currentStep !== RescheduleStep.Date ? (
            <StepSummary
              time={time}
              selectedDate={date!}
              isLastStep={isLastStep}
              onNextStep={goToNextStep}
              onPreviousStep={goToPreviousStep}
              isStepComplete={isStepComplete}
              requiresAdditionalPayment={requiresAdditionalPayment}
            >
              {currentStep === RescheduleStep.Time ? (
                <TimeContainer onSelectTime={selectTime} time={time} scheduleTimes={scheduleTimes} />
              ) : null}

              {isLastStep && date ? (
                <FeatureFlag
                  feature='PR-3691-charged-reschedule'
                  active={() => (
                    <RescheduleSummary
                      date={date}
                      time={time}
                      saleItem={saleItem}
                      chargeSummary={chargeSummary}
                      requiresAdditionalPayment={requiresAdditionalPayment}
                      remainingRescheduleCount={saleItem.rescheduleInfo.remainingRescheduleCount}
                    />
                  )}
                />
              ) : null}
            </StepSummary>
          ) : null}

          <SpinnerWrapper hidden={!isRescheduleOngoing}>
            <Spinner visible={isRescheduleOngoing && !currentStep} spinnerColor={actionColor} />
          </SpinnerWrapper>
        </Container>
      </Wrapper>
    </BackdropModal>
  );
};

export default RescheduleModal;

const Wrapper = styled.article`
  width: 100%;
  pointer-events: all;
  overflow: hidden;
  max-width: 744px !important;
  margin: 0 auto;
  margin-top: 120px;

  ${device.smallTablet} {
    border-radius: 8px;
  }
`;

const Container = styled.section`
  background-color: white;
  position: relative;
  min-height: min-content;
  width: 100%;
  padding: 24px;
`;

const SpinnerWrapper = styled.div`
  width: 82px;
  height: 82px;
  margin: 0 auto;
`;
